import React, {useRef, useState} from 'react'
import { useHistory } from 'react-router';
import {login, checkIsSetupComplete} from "utils";
import { checkVersionAndRefresh } from "utils/versionCheck";
import { config } from "api";

import {
    Page,
    Container
} from "./Login.styles";
import {StyledButton} from "../../../components/Buttons/DefaultButton/DefaultButton.styles";
import {FormMaterialInput} from "../../../components/Inputs/MaterialInput";
import SignUpHeader from "../../../components/SignUpHeader";
import * as ls from "utils/localStorage";
import Api from "../../../api";
import HeaderStatusBar from "../../../components/HeaderStatusBar";
import {useForm} from "react-hook-form";
import {joiResolver} from "@hookform/resolvers/joi";
import joi from "joi";
import {usePersistedState} from "../../../hooks";
import entities from "../../../reduxStore/entityKeys";
import {HYGIENIST_TYPES} from "utils/types";
import useQueryParams from 'hooks/useQueryParams';
import FixedLoader from "../../../components/FixedLoader";
import { resolveLoginErrorMessage, GENERIC_LOGIN_ERROR } from "./Login.errors";
import { openExternalUrl } from "../../../utils/nativeApp";

// @ts-ignore
const TempStars = window.TempStars;
// @ts-ignore
const app = window.app as any;
// @ts-ignore
const mainView = window.mainView as any;

const schema = joi
    .object({
        email: joi.string().email({ tlds: { allow: false } }).label('Login').required(),
        password: joi.string().label('Password').required(),
    }).required()

const TERMS_URL = 'https://www.tempstars.ca/terms';
const PRIVACY_URL = 'https://www.tempstars.ca/privacy';

export const focusInputForIosNativeTap = (input: HTMLInputElement | null): void => {
    const nativeWindow = window as any;
    const isIosNative = !!nativeWindow.cordova
        && (nativeWindow.cordova?.platformId === 'ios' || nativeWindow.device?.platform === 'iOS');

    if (!isIosNative || !input || input.disabled || input.readOnly) return;

    input.focus({ preventScroll: true });
}

export default function Login() {
    const history = useHistory();
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const emailInputRef = useRef<HTMLInputElement | null>(null);
    const passwordInputRef = useRef<HTMLInputElement | null>(null);
    ls.remove('pageData');

    const { payInvoice, jobPost, shiftDate } = useQueryParams();

    const setUser = usePersistedState({
        entityName: entities.user,
    })[1];

    const goNext = () => {
        history.push({
            pathname: 'landing'
        });
    }

    const goForgotPassword = () => {
        history.push({
            pathname: 'ForgotPassword'
        });
    }

    const openAuthExternalLink = (url: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        event.stopPropagation();
        openExternalUrl(url);
    }

    const {
        control,
        handleSubmit,
        setError,
        clearErrors,
    } = useForm({
        resolver: joiResolver(schema) as any,
    });

    const onSubmit = async (event: any) => {
        const {email, password} = event
        
        // Clear previous errors and error message before attempting login
        clearErrors();
        setErrorMessage('');

        try {
            setIsLoading(true);
            const userProps = await login( email, password, setUser );
            
            try {
                const versionOk = await checkVersionAndRefresh(config.server.baseUrl);
                if (!versionOk) {
                    setIsLoading(false);
                    return;
                }
            } catch (versionError) {
                console.error('Version check failed after login:', versionError);
            }
            
            const isSetupComplete = checkIsSetupComplete(userProps)
            if (isSetupComplete) {
                await TempStars.App.gotoStartingPage1();

                /* Query params redirection check */
                if (shiftDate != "") {
                    setIsLoading(false);
                    return;
                }
                if (payInvoice != "") {
                    setIsLoading(false);
                    return;
                }
                if (jobPost != "") {
                    setIsLoading(false);
                    return;
                }

                if (userProps.hygienistId &&
                    (userProps.hygienist?.type === HYGIENIST_TYPES.FRONT_DESK
                        || userProps.hygienist?.type === HYGIENIST_TYPES.ASSOCIATE_DENTIST)) {
                    TempStars.Analytics.track('log in')
                    history.push({
                        pathname: 'jobPostsList'
                    })
                } else {
                    history.push('loading')
                }
            } else {
                const { roleId } = await Api.getRole();
                setIsLoading(false);
                let pathname = roleId === 4
                    ? 'SignUp1_DO'
                    : 'SignUp1_Talent'
                if (!!userProps?.hygienistId && !!userProps?.hygienist?.firstName) {
                    pathname = 'SignUp2_Talent'
                }
                history.push({
                    pathname: pathname,
                })
            }
            setIsLoading(false);
        } catch (e: any) {
            TempStars.Analytics.track('login - error')
            setIsLoading(false);
            console.error(e)
            // Resolve the user-facing message: specific copy for deactivated
            // (deleted) accounts, generic credentials message otherwise.
            // The generic message is used for the per-field errors so we never
            // leak raw backend/DB errors into the individual inputs.
            const errorMessageToShow = resolveLoginErrorMessage(e);
            setError("email", {type: "manual", message: GENERIC_LOGIN_ERROR})
            setError("password", {type: "manual", message: GENERIC_LOGIN_ERROR})
            return setErrorMessage(errorMessageToShow);
        }
    }

    return (
        <Page>
            <Container data-page="login" className={'no-swipeback'}>
                <HeaderStatusBar />

                <div className={"loginForm"}>
                    <SignUpHeader
                        title= {'Login'}
                        error={errorMessage}
                    />

                    <div className="contentBlock">
                        <form id="login-form" onSubmit={handleSubmit(onSubmit)}>
                            <FormMaterialInput
                                className={'inset no-fastclick'}
                                control={control}
                                name={'email'}
                                label={'Login'}
                                type="email"
                                inputRef={emailInputRef}
                                onTouchStartCapture={() => focusInputForIosNativeTap(emailInputRef.current)}
                                onTouchEndCapture={() => focusInputForIosNativeTap(emailInputRef.current)}
                            />
                            <FormMaterialInput
                                className={'inset no-fastclick'}
                                control={control}
                                name={'password'}
                                label={'Password'}
                                type={'password'}
                                inputRef={passwordInputRef}
                                onTouchStartCapture={() => focusInputForIosNativeTap(passwordInputRef.current)}
                                onTouchEndCapture={() => focusInputForIosNativeTap(passwordInputRef.current)}
                            />
                            <StyledButton
                                variant="contained"
                                color="primary"
                                size="large"
                                id="signup-button"
                                type={'submit'}
                            >
                                Login
                            </StyledButton>
                        </form>
                    </div>

                    <div className="dont-have-an-account-sign-up-wrapper">
                        <div className="dont-have-an-container">
                            <span>Don't have an account? </span>
                            <div className="underlinedLink" onClick={goNext}>Sign Up</div>
                        </div>
                    </div>

                    <div className="terms-of-service-privacy-pol-wrapper">
                        <div className="terms-of-service-container">
                            <a className="underlinedLink external externalLink no-fastclick"
                               href={TERMS_URL}
                               target="_system"
                               aria-label="Terms of Service"
                               data-testid="login-terms-of-service-link"
                               content-desc="login-terms-of-service-link"
                               onClick={openAuthExternalLink(TERMS_URL)}
                            >
                                Terms of Service
                            </a>
                            <span className="span1"> & </span>
                            <a className="underlinedLink external externalLink no-fastclick"
                               href={PRIVACY_URL}
                               target="_system"
                               aria-label="Privacy Policy"
                               data-testid="login-privacy-policy-link"
                               content-desc="login-privacy-policy-link"
                               onClick={openAuthExternalLink(PRIVACY_URL)}
                            >
                                Privacy Policy
                            </a>
                        </div>
                    </div>

                    <div className="forgotten-password-parent">
                        <div className="underlinedLink" onClick={goForgotPassword}>Forgot Password?</div>
                    </div>

                </div>

                <div className="footer footerLinks"></div>
            </Container>
            <FixedLoader loading={isLoading} />
        </Page>
    )
}
